import { Loadable, Loading } from '@/components/loaders';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { templates, widgets } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FormFields/StyledField';
import { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { Button } from '@/lib';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { PrepareConnectorFlowStepResponse, useCreateFlowInstanceMutation, usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import Form from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmailSendingDomainConnector } from './useEmailSendingDomainConnector';

type StepFormConfig = {
  schema: RJSFSchema;
  uiSchema: Record<string, unknown>;
  title?: string;
  description?: string;
};

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type EmailSendingDomainInsertFlowProps = {
  onBack: () => void;
  onCreated: () => void;
};

const getStepFormConfig = (prepareResult: PrepareStepResult | null): StepFormConfig | null => {
  const stepConfig = (prepareResult as Record<string, unknown> | null)?.['step1'] as
    | { reactJsonSchemaForm?: StepFormConfig & { formId?: string } }
    | undefined;

  if (!stepConfig?.reactJsonSchemaForm?.schema) {
    return null;
  }

  return stepConfig.reactJsonSchemaForm;
};

export const EmailSendingDomainInsertFlow: FC<EmailSendingDomainInsertFlowProps> = ({ onBack, onCreated }) => {
  const { t } = useTranslation('campaign');
  const workspace = useWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const appDispatch = useAppDispatch();
  const { flowId, connectorInstanceId, isLoading: isConnectorLoading, isReady, missingFlow, missingInstance } = useEmailSendingDomainConnector();

  const [prepareStep] = usePrepareConnectorFlowStepMutation();
  const [createFlowInstance, { isLoading: isCreating }] = useCreateFlowInstanceMutation();

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [isPrepareLoading, setIsPrepareLoading] = useState(true);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrepareData = async () => {
      if (!flowId || !connectorInstanceId || !audienceId) {
        setIsPrepareLoading(false);
        return;
      }

      try {
        setIsPrepareLoading(true);
        setPrepareError(null);
        const response = await prepareStep({
          workspaceId: workspace.id,
          flowId,
          audienceId,
          stepId: 'step1',
          stepData: {},
          connectorInstanceId,
        }).unwrap();

        const translatedResponse = translateI18nValues(response, currentLanguage);
        setPrepareResult(translatedResponse?.configuration?.elements?.params as PrepareStepResult);
      } catch {
        setPrepareError(String(t('emailWorkspace.domains.prepareFailed')));
      } finally {
        setIsPrepareLoading(false);
      }
    };

    if (isReady) {
      void fetchPrepareData();
    } else if (!isConnectorLoading) {
      setIsPrepareLoading(false);
    }
  }, [audienceId, connectorInstanceId, currentLanguage, flowId, isConnectorLoading, isReady, prepareStep, t, workspace.id]);

  const stepForm = useMemo(() => getStepFormConfig(prepareResult), [prepareResult]);
  const sendingDomain = String(formData['sending_domain'] ?? '').trim();
  const canSubmit = !!sendingDomain && !!stepForm && !isCreating;

  const handleConfirm = async () => {
    if (!flowId || !connectorInstanceId || !audienceId || !canSubmit) {
      return;
    }

    try {
      setSubmitError(null);
      const payload = buildFlowInstancePayload({
        workspaceId: workspace.id,
        connectorFlowId: flowId,
        connectorInstanceId,
        audienceId,
        flowParams: formData,
        flowMappings: {},
      });

      await createFlowInstance(payload).unwrap();
      appDispatch(
        showToast({
          type: ToastType.SUCCESS,
          title: String(t('emailWorkspace.domains.createSuccess')),
        })
      );
      onCreated();
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('emailWorkspace.domains.createFailed'));
      setSubmitError(errorMessage);
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
    }
  };

  if (isConnectorLoading || isPrepareLoading || isAudienceLoading) {
    return <Loading />;
  }

  if (isAudienceSettled && !hasAudience) {
    return (
      <div className='space-y-6'>
        <AudienceRequiredEmptyState />
        <Button intent='secondary' className='mt-4' onClick={onBack} text={t('emailWorkspace.domains.back')} />
      </div>
    );
  }

  if (missingFlow || missingInstance) {
    return (
      <div className='rounded-2xl border border-secondary-border bg-white p-6 md:p-8'>
        <p className='text-secondary-600 text-sm'>
          {missingFlow ? t('emailWorkspace.domains.missingInsertFlow') : t('emailWorkspace.domains.missingConnectorInstance')}
        </p>
        <Button intent='secondary' className='mt-4' onClick={onBack} text={t('emailWorkspace.domains.back')} />
      </div>
    );
  }

  return (
    <div className='space-y-6 rounded-2xl border border-secondary-border bg-white p-6 md:p-8'>
      <div>
        <h2 className='text-xl font-bold text-slate-900'>{t('emailWorkspace.domains.addTitle')}</h2>
        <p className='text-secondary-600 mt-1 text-sm'>{t('emailWorkspace.domains.addDescription')}</p>
      </div>

      <div className='flex items-center gap-3 border-b border-secondary-border pb-4'>
        <span className='flex size-7 items-center justify-center rounded-full bg-blue-700 text-sm font-bold text-white'>1</span>
        <span className='text-sm font-semibold text-secondary-600'>{t('emailWorkspace.domains.stepLabel')}</span>
      </div>

      <Loadable isLoading={false}>
        {prepareError ? <p className='text-sm text-red-600'>{prepareError}</p> : null}
        {stepForm ? (
          <Form
            schema={stepForm.schema}
            uiSchema={{
              ...stepForm.uiSchema,
              'ui:submitButtonOptions': { norender: true },
            }}
            formData={formData}
            onChange={({ formData: nextFormData }) => setFormData(nextFormData)}
            validator={validator}
            templates={templates}
            widgets={widgets}
          >
            <Fragment />
          </Form>
        ) : (
          !prepareError && <p className='text-secondary-600 text-sm'>{t('emailWorkspace.domains.prepareFailed')}</p>
        )}
      </Loadable>

      {submitError ? <p className='text-sm text-red-600'>{submitError}</p> : null}

      <div className='flex flex-wrap items-center justify-between gap-3'>
        <Button intent='secondary' onClick={onBack} text={t('emailWorkspace.domains.back')} />
        <Button intent='primary' isLoading={isCreating} disabled={!canSubmit} onClick={handleConfirm} text={t('emailWorkspace.domains.confirm')} />
      </div>
    </div>
  );
};
